// ---------------------------------------------------------------------------
// ui.js — Shared Component Library (Warm Stone Design System)
// Shadow Fund Admin
// ---------------------------------------------------------------------------
// All components pull from theme.js. No hardcoded colors anywhere.
// ---------------------------------------------------------------------------
import React from "react";
import t from "./theme";  // ← imports the default export from theme.js

// ═══════════════════════════════════════════════════════════════════════════
// CARD — primary container for all content sections
// ═══════════════════════════════════════════════════════════════════════════
export function Card({ title, subtitle, action, children, style, noPad, flat }) {
  return (
    <div
      style={{
        background: t.bgCard,
        border: `1px solid ${t.border}`,
        borderRadius: t.radiusMd,
        boxShadow: flat ? "none" : t.shadow,
        marginBottom: 16,
        ...style,
      }}
    >
      {(title || action) && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "14px 20px",
            borderBottom: `1px solid ${t.borderLight}`,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: t.text,
                fontFamily: t.fontBody,
              }}
            >
              {title}
            </div>
            {subtitle && (
              <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2 }}>
                {subtitle}
              </div>
            )}
          </div>
          {action}
        </div>
      )}
      <div style={noPad ? {} : { padding: "16px 20px" }}>{children}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PAGE HEADER — top-of-page title + optional right-side action area
// ═══════════════════════════════════════════════════════════════════════════
export function PageHeader({ title, subtitle, action }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 20,
        gap: 16,
        flexWrap: "wrap",
      }}
    >
      <div>
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 700,
            color: t.text,
            fontFamily: t.fontDisplay,
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <p
            style={{
              margin: "4px 0 0",
              fontSize: 13,
              color: t.textMuted,
              fontFamily: t.fontBody,
            }}
          >
            {subtitle}
          </p>
        )}
      </div>
      {action && <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>{action}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// STAT CARD / KPI CARD — single metric display
// ═══════════════════════════════════════════════════════════════════════════
export function StatCard({ label, value, change, up, icon, color, accent }) {
  return (
    <div
      style={{
        flex: "1 1 180px",
        minWidth: 160,
        background: t.kpiBg,
        border: `1px solid ${t.kpiBorder}`,
        // An optional stripe in a semantic colour, for a row of cards that
        // reads as one colour otherwise. Pages that do not pass it are
        // unchanged.
        borderLeft: accent ? `3px solid ${accent}` : `1px solid ${t.kpiBorder}`,
        borderRadius: t.radius,
        padding: "14px 16px",
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: t.textMuted,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: 6,
          fontFamily: t.fontBody,
        }}
      >
        {icon && <span style={{ marginRight: 6 }}>{icon}</span>}
        {label}
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 700,
          color: color || t.text,
          fontFamily: t.fontDisplay,
          lineHeight: 1.2,
        }}
      >
        {value}
      </div>
      {change !== undefined && change !== null && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 500,
            color:
              up === true ? t.success : up === false ? t.danger : t.textMuted,
            marginTop: 4,
            fontFamily: t.fontBody,
          }}
        >
          {up === true && "▲ "}
          {up === false && "▼ "}
          {change}
        </div>
      )}
    </div>
  );
}

// Alias
export const KPICard = StatCard;

// ═══════════════════════════════════════════════════════════════════════════
// BADGE — status/label pills
// ═══════════════════════════════════════════════════════════════════════════
const badgeStyles = {
  green: { bg: t.successBg, color: t.success, border: t.successBorder },
  red: { bg: t.dangerBg, color: t.danger, border: t.dangerBorder },
  yellow: { bg: t.warningBg, color: t.warning, border: t.warningBorder },
  blue: { bg: t.infoBg, color: t.info, border: t.infoBorder },
  amber: { bg: t.accentLight, color: t.accent, border: t.accentBorder },
  gray: {
    bg: "rgba(138,128,120,0.08)",
    color: t.textMuted,
    border: "rgba(138,128,120,0.18)",
  },
};

export function Badge({ text, variant = "gray", dot, style: customStyle }) {
  const s = badgeStyles[variant] || badgeStyles.gray;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 10px",
        borderRadius: t.radiusFull,
        fontSize: 11,
        fontWeight: 600,
        fontFamily: t.fontBody,
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
        whiteSpace: "nowrap",
        lineHeight: "16px",
        ...customStyle,
      }}
    >
      {dot && (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: s.color,
            flexShrink: 0,
          }}
        />
      )}
      {text}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// BUTTON — primary, secondary, danger, success, ghost variants
// ═══════════════════════════════════════════════════════════════════════════
const buttonVariants = {
  primary: {
    background: t.accent,
    color: t.textOnAccent,
    border: "none",
    hoverBg: t.accentHover,
  },
  secondary: {
    background: t.bgCard,
    color: t.text,
    border: `1.5px solid ${t.border}`,
    hoverBg: t.bgSubtle,
  },
  danger: {
    background: t.danger,
    color: "#fff",
    border: "none",
    hoverBg: "#A44A40",
  },
  success: {
    background: t.success,
    color: "#fff",
    border: "none",
    hoverBg: "#4E7A50",
  },
  ghost: {
    background: "transparent",
    color: t.accent,
    border: "none",
    hoverBg: t.accentLight,
  },
  outline: {
    background: "transparent",
    color: t.accent,
    border: `1.5px solid ${t.accent}`,
    hoverBg: t.accentLight,
  },
};

export function Button({
  children,
  onClick,
  variant = "primary",
  disabled,
  size = "md",
  style: customStyle,
  type = "button",
  title,
}) {
  const v = buttonVariants[variant] || buttonVariants.primary;
  const [hovered, setHovered] = React.useState(false);

  const sizeStyles = {
    sm: { padding: "5px 12px", fontSize: 12 },
    md: { padding: "8px 16px", fontSize: 13 },
    lg: { padding: "10px 22px", fontSize: 14 },
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: disabled
          ? t.borderLight
          : hovered
          ? v.hoverBg
          : v.background,
        color: disabled ? t.textDisabled : v.color,
        border: v.border,
        borderRadius: t.radius,
        fontWeight: 600,
        fontFamily: t.fontBody,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: t.transitionFast,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        lineHeight: 1,
        ...sizeStyles[size],
        ...customStyle,
      }}
    >
      {children}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DATA TABLE — sortable, hoverable, themed
// ═══════════════════════════════════════════════════════════════════════════
export function DataTable({
  columns,
  rows,
  onRowClick,
  emptyMessage,
  loading,
  striped,
}) {
  if (loading) return <LoadingSpinner />;

  if (!rows || rows.length === 0) {
    return <EmptyState message={emptyMessage || "No data"} />;
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 13,
          fontFamily: t.fontBody,
        }}
      >
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                style={{
                  textAlign: col.align || "left",
                  padding: "9px 14px",
                  borderBottom: `2px solid ${t.border}`,
                  background: t.tableHeaderBg,
                  color: t.textMuted,
                  fontWeight: 600,
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  whiteSpace: "nowrap",
                  minWidth: col.minWidth || "auto",
                  fontFamily: t.fontBody,
                }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={row.id || i}
              onClick={() => onRowClick && onRowClick(row)}
              style={{
                cursor: onRowClick ? "pointer" : "default",
                borderBottom: `1px solid ${t.borderLight}`,
                background:
                  striped && i % 2 === 1 ? t.tableStripeBg : "transparent",
                transition: t.transitionFast,
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = t.tableRowHover)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background =
                  striped && i % 2 === 1 ? t.tableStripeBg : "transparent")
              }
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  style={{
                    padding: "10px 14px",
                    textAlign: col.align || "left",
                    color: t.text,
                    whiteSpace: col.wrap ? "normal" : "nowrap",
                    minWidth: col.minWidth || "auto",
                    maxWidth: col.maxWidth || "none",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontFamily: col.mono ? t.fontMono : t.fontBody,
                    fontSize: col.mono ? 12 : 13,
                  }}
                >
                  {col.render
                    ? col.render(row[col.key], row, i)
                    : row[col.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MONEY — formatted currency display with optional P&L coloring
// ═══════════════════════════════════════════════════════════════════════════
export function Money({ value, showSign, currency = "USD" }) {
  if (value === null || value === undefined || value === "")
    return <span style={{ color: t.textMuted }}>—</span>;

  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num))
    return <span style={{ color: t.textMuted }}>—</span>;

  const formatted = Math.abs(num).toLocaleString("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const color = showSign
    ? num > 0
      ? t.success
      : num < 0
      ? t.danger
      : t.text
    : t.text;
  const sign = showSign && num > 0 ? "+" : "";

  return (
    <span
      style={{
        color,
        fontVariantNumeric: "tabular-nums",
        fontFamily: t.fontMono,
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      {num < 0 ? "-" : sign}
      {formatted}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MODAL — centered overlay with backdrop
// ═══════════════════════════════════════════════════════════════════════════
export function Modal({ open, onClose, title, width, children, footer }) {
  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(44, 40, 37, 0.45)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: t.bgCard,
          borderRadius: t.radiusLg,
          boxShadow: t.shadowLg,
          width: width || 520,
          maxWidth: "95vw",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 20px",
            borderBottom: `1px solid ${t.borderLight}`,
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 700,
              color: t.text,
              fontFamily: t.fontDisplay,
            }}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 18,
              color: t.textMuted,
              padding: 4,
              lineHeight: 1,
              borderRadius: t.radiusSm,
              transition: t.transitionFast,
            }}
            onMouseEnter={(e) => (e.target.style.color = t.text)}
            onMouseLeave={(e) => (e.target.style.color = t.textMuted)}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "16px 20px", overflowY: "auto", flex: 1 }}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              padding: "12px 20px",
              borderTop: `1px solid ${t.borderLight}`,
              background: t.bgSubtle,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FILTER PILLS — toggle buttons for filtering tables
// ═══════════════════════════════════════════════════════════════════════════
export function FilterPill({ label, active, onClick, count }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "5px 14px",
        borderRadius: t.radiusFull,
        border: active ? `1.5px solid ${t.accent}` : `1px solid ${t.border}`,
        background: active ? t.accentLight : t.bgCard,
        color: active ? t.accent : t.textSecondary,
        fontSize: 12,
        fontWeight: active ? 600 : 500,
        fontFamily: t.fontBody,
        cursor: "pointer",
        transition: t.transitionFast,
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
      }}
    >
      {label}
      {count !== undefined && (
        <span
          style={{
            background: active ? t.accent : t.border,
            color: active ? "#fff" : t.textMuted,
            borderRadius: t.radiusFull,
            padding: "1px 7px",
            fontSize: 10,
            fontWeight: 600,
            lineHeight: "14px",
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TABS — horizontal tab bar
// ═══════════════════════════════════════════════════════════════════════════
export function Tabs({ tabs, active, onChange }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 0,
        borderBottom: `2px solid ${t.borderLight}`,
        marginBottom: 16,
      }}
    >
      {tabs.map((tab) => {
        const isActive = active === (tab.key || tab);
        const label = tab.label || tab;
        const key = tab.key || tab;
        return (
          <button
            key={key}
            onClick={() => onChange(key)}
            style={{
              padding: "10px 18px",
              border: "none",
              borderBottom: isActive
                ? `2px solid ${t.accent}`
                : "2px solid transparent",
              background: "transparent",
              color: isActive ? t.accent : t.textMuted,
              fontSize: 13,
              fontWeight: isActive ? 600 : 500,
              fontFamily: t.fontBody,
              cursor: "pointer",
              transition: t.transitionFast,
              marginBottom: -2,
            }}
          >
            {label}
            {tab.count !== undefined && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 10,
                  fontWeight: 600,
                  color: isActive ? t.accent : t.textDisabled,
                }}
              >
                ({tab.count})
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FORM ELEMENTS — Input, Select, TextArea, Checkbox, FormField
// ═══════════════════════════════════════════════════════════════════════════
const inputBase = {
  width: "100%",
  padding: "8px 12px",
  border: `1px solid ${t.border}`,
  borderRadius: t.radius,
  fontSize: 13,
  fontFamily: t.fontBody,
  color: t.text,
  background: t.bgInput,
  outline: "none",
  transition: t.transitionFast,
  boxSizing: "border-box",
};

export function Input({ label, error, style: customStyle, ...props }) {
  return (
    <div style={{ marginBottom: 12 }}>
      {label && (
        <label
          style={{
            display: "block",
            fontSize: 12,
            fontWeight: 600,
            color: t.textSecondary,
            marginBottom: 4,
            fontFamily: t.fontBody,
          }}
        >
          {label}
        </label>
      )}
      <input
        {...props}
        style={{ ...inputBase, ...customStyle }}
        onFocus={(e) => {
          e.target.style.borderColor = t.borderFocus;
          e.target.style.boxShadow = t.shadowFocus;
          props.onFocus && props.onFocus(e);
        }}
        onBlur={(e) => {
          e.target.style.borderColor = t.border;
          e.target.style.boxShadow = "none";
          props.onBlur && props.onBlur(e);
        }}
      />
      {error && (
        <div
          style={{ fontSize: 11, color: t.danger, marginTop: 3, fontFamily: t.fontBody }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

export function Select({ label, options, error, style: customStyle, ...props }) {
  return (
    <div style={{ marginBottom: 12 }}>
      {label && (
        <label
          style={{
            display: "block",
            fontSize: 12,
            fontWeight: 600,
            color: t.textSecondary,
            marginBottom: 4,
            fontFamily: t.fontBody,
          }}
        >
          {label}
        </label>
      )}
      <select
        {...props}
        style={{
          ...inputBase,
          cursor: "pointer",
          ...customStyle,
        }}
      >
        {options.map((opt) => (
          <option
            key={opt.value ?? opt}
            value={opt.value ?? opt}
          >
            {opt.label ?? opt}
          </option>
        ))}
      </select>
      {error && (
        <div style={{ fontSize: 11, color: t.danger, marginTop: 3 }}>{error}</div>
      )}
    </div>
  );
}

export function TextArea({ label, error, ...props }) {
  return (
    <div style={{ marginBottom: 12 }}>
      {label && (
        <label
          style={{
            display: "block",
            fontSize: 12,
            fontWeight: 600,
            color: t.textSecondary,
            marginBottom: 4,
            fontFamily: t.fontBody,
          }}
        >
          {label}
        </label>
      )}
      <textarea
        {...props}
        style={{
          ...inputBase,
          minHeight: 80,
          resize: "vertical",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = t.borderFocus;
          e.target.style.boxShadow = t.shadowFocus;
        }}
        onBlur={(e) => {
          e.target.style.borderColor = t.border;
          e.target.style.boxShadow = "none";
        }}
      />
      {error && (
        <div style={{ fontSize: 11, color: t.danger, marginTop: 3 }}>{error}</div>
      )}
    </div>
  );
}

export function FormField({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      {label && (
        <label
          style={{
            display: "block",
            fontSize: 12,
            fontWeight: 600,
            color: t.textSecondary,
            marginBottom: 4,
            fontFamily: t.fontBody,
          }}
        >
          {label}
        </label>
      )}
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// LOADING SPINNER
// ═══════════════════════════════════════════════════════════════════════════
export function LoadingSpinner({ message }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 48,
        gap: 12,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          border: `3px solid ${t.borderLight}`,
          borderTopColor: t.accent,
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }}
      />
      {message && (
        <div style={{ fontSize: 13, color: t.textMuted, fontFamily: t.fontBody }}>
          {message}
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// EMPTY STATE — shown when a table or section has no data
// ═══════════════════════════════════════════════════════════════════════════
export function EmptyState({ message, icon, action }) {
  return (
    <div
      style={{
        textAlign: "center",
        padding: "40px 20px",
        color: t.textMuted,
      }}
    >
      {icon && <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.5 }}>{icon}</div>}
      <div style={{ fontSize: 13, fontFamily: t.fontBody }}>{message}</div>
      {action && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DIVIDER — horizontal line
// ═══════════════════════════════════════════════════════════════════════════
export function Divider({ spacing = 16 }) {
  return (
    <hr
      style={{
        border: "none",
        borderTop: `1px solid ${t.borderLight}`,
        margin: `${spacing}px 0`,
      }}
    />
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TOOLTIP — simple hover tooltip (CSS only)
// ═══════════════════════════════════════════════════════════════════════════
export function Tooltip({ text, children }) {
  const [show, setShow] = React.useState(false);
  return (
    <span
      style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <span
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            left: "50%",
            transform: "translateX(-50%)",
            background: t.sidebar,
            color: t.textOnDark,
            padding: "5px 10px",
            borderRadius: t.radiusSm,
            fontSize: 11,
            fontFamily: t.fontBody,
            whiteSpace: "nowrap",
            zIndex: 999,
            boxShadow: t.shadowMd,
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}
